import { Charity } from "../../../../../models/Charity";
import { USDC } from "../../../../../models/UsdcValue";
import {
  CURRENCY,
  MAXIMUM_INSTITUTION_LENGTH,
} from "../../../../../utils/Constants";
import ball from "../../../../../assets/images/bermuda_ball.png";
import { useSpring, animated, easings } from "react-spring";
import { mumboJumbo } from "../../../../../utils/Utils";

export interface RaisingForCharityProps {
  /**
   * @description Charity institution.
   */
  charity: Charity;
  /**
   * @description Value raised for the institution.
   */
  value: USDC | undefined;
  /**
   * @description Goal value to be raised.
   */
  goal: USDC | undefined;
}

/**
 * @description Charity component. Contains the name of the institution,
 * the value raised, and the goal. Also links to the charity's website.
 */
export default function RaisingForCharity(props: RaisingForCharityProps) {
  const raisingAnimation = useSpring({
    from: { number: 0 },
    to: { number: Number(props.value?.value) },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
  });
  return (
    <div data-aos="fade-down" data-aos-delay="200">
      <div className="flex flex-row">
        <div
          key={props.value?.value}
          className="~mt-2/3 ~mb-2/3 animate-in spin-in-3 hover:scale-105 duration-250"
        >
          {/**
           * Institution
           */}
          <div className="~mb-0.5/1 flex flex-row">
            <p className="font-barlowc font-bold ~text-base/lg bg-gradient-to-b from-bermuda to-ming bg-clip-text text-transparent">
              RAISING FOR&nbsp;
            </p>
            <span
              onClick={() => window.open(props.charity.website, "_blank")}
              className="cursor-pointer underline font-barlowc font-bold ~text-base/lg text-bermuda"
            >
              {props.charity.name
                .substring(0, MAXIMUM_INSTITUTION_LENGTH)
                .toUpperCase()}
              {props.charity.name.length > MAXIMUM_INSTITUTION_LENGTH
                ? "..."
                : ""}
            </span>
          </div>
          {/**
           * Charity Value
           */}
          <div className="flex flex-row">
            <animated.div
              className={`font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor`}
            >
              {raisingAnimation.number.to((n) =>
                mumboJumbo(n, props.value?.pretty.length ?? 0),
              )}
            </animated.div>
            <p className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-b from-bermuda to-ming">
              &nbsp;{CURRENCY}
            </p>
            <p
              className={`font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor`}
            >
              &nbsp;/
            </p>
          </div>
          {/**
           * Charity Goal
           */}
          <div className="flex flex-row ~mt-0.5/1">
            <p
              className={`font-archb font-bold ~text-xl/xxxl leading-6 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor`}
            >
              {props.goal?.pretty ?? "..."}
            </p>
            <p className="font-archb font-bold ~text-xl/xxxl leading-6 bg-clip-text text-transparent bg-gradient-to-b from-bermuda to-ming">
              &nbsp;{CURRENCY}
            </p>
          </div>
        </div>
        <img
          src={ball}
          className="select-none ~ml-7/8 ~h-9/11 ~w-9/11 animate-bounce delay-500"
          alt="mango-ball"
        />
      </div>
    </div>
  );
}
